import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { LOGO_URL, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";

const formatError = (detail) => {
  if (!detail) return "Algo salió mal";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const LoginDialog = ({ open, onOpenChange }) => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [role, setRole] = useState("client");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const goAuth = (r) => {
    sessionStorage.setItem("sa_signup_role", r);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await api.post("/auth/login", { email, password });
        setUser(r.data.user);
        toast.success("Bienvenido");
        onOpenChange(false);
        if (r.data.user.role === "admin") navigate("/admin");
        else if (r.data.user.role === "model") navigate("/dashboard/model");
        else navigate("/dashboard");
      } else {
        const r = await api.post("/auth/register", { email, password, name, role });
        setUser(r.data.user);
        toast.success("Cuenta creada");
        onOpenChange(false);
        if (r.data.user.role === "model") navigate("/dashboard/model");
        else navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="login-dialog" className="max-w-md bg-[#FAF9F6] border-[#E5E2D9]">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <img src={LOGO_URL} alt="Secret Angels" className="h-20 w-20 object-contain" />
          </div>
          <DialogTitle className="text-center font-serif text-3xl tracking-wide">Bienvenido</DialogTitle>
          <DialogDescription className="text-center text-[#6B6A63]">
            Accede al directorio más exclusivo
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-[#F3F1EA]">
            <TabsTrigger data-testid="tab-login" value="login">Iniciar sesión</TabsTrigger>
            <TabsTrigger data-testid="tab-register" value="register">Registrarse</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-3 pt-4">
            <form onSubmit={handleEmailAuth} className="space-y-3">
              <div>
                <Label className="text-xs text-[#6B6A63]">Correo</Label>
                <div className="relative">
                  <Mail size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A63]" />
                  <Input
                    data-testid="login-email"
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="pl-9 bg-white border-[#E5E2D9]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B6A63]">Contraseña</Label>
                <div className="relative">
                  <Lock size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A63]" />
                  <Input
                    data-testid="login-password"
                    type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-9 bg-white border-[#E5E2D9]"
                  />
                </div>
              </div>
              <button data-testid="login-submit" disabled={busy} type="submit" className="w-full btn-primary disabled:opacity-60">
                {busy ? "Entrando…" : "Iniciar sesión"}
              </button>
            </form>

            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#E5E2D9]" /></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                <span className="bg-[#FAF9F6] px-2 text-[#6B6A63]">o continúa con</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button data-testid="google-login-client" onClick={() => goAuth("client")} className="btn-ghost text-sm">
                Google · Cliente
              </button>
              <button data-testid="google-login-model" onClick={() => goAuth("model")} className="btn-ghost text-sm">
                Google · Modelo
              </button>
            </div>
          </TabsContent>

          <TabsContent value="register" className="space-y-3 pt-4">
            <form onSubmit={handleEmailAuth} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRole("client")}
                  className={`text-sm py-2 rounded-full border transition ${role === 'client' ? 'bg-[#364531] text-[#FAF9F6] border-[#364531]' : 'border-[#E5E2D9] text-[#6B6A63]'}`}
                  data-testid="register-role-client"
                >Soy Cliente</button>
                <button
                  type="button"
                  onClick={() => setRole("model")}
                  className={`text-sm py-2 rounded-full border transition ${role === 'model' ? 'bg-[#364531] text-[#FAF9F6] border-[#364531]' : 'border-[#E5E2D9] text-[#6B6A63]'}`}
                  data-testid="register-role-model"
                >Soy Modelo</button>
              </div>

              <div>
                <Label className="text-xs text-[#6B6A63]">Nombre</Label>
                <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={role === "model" ? "Nombre artístico" : "Tu nombre"} className="bg-white border-[#E5E2D9]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6A63]">Correo</Label>
                <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="tu@correo.com" className="bg-white border-[#E5E2D9]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6A63]">Contraseña (mín. 6)</Label>
                <Input data-testid="register-password" type="password" required minLength={6} value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white border-[#E5E2D9]" />
              </div>
              <button data-testid="register-submit" disabled={busy} type="submit" className="w-full btn-primary disabled:opacity-60">
                {busy ? "Creando…" : "Crear cuenta"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
